import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { createWorkspace, updateWorkspaceContent } from '../../../store/auth';
import { useAuth } from './AuthContext';
import { useSelector, useDispatch } from 'react-redux';
import { markAsSaved } from 'store/workspaceSlice';
import { CiCircleInfo } from "react-icons/ci";

// Enhanced CustomConfirm Component - Same as Space component
const CustomConfirm = ({ message, onConfirm, onCancel }) => {
  const config = {
    bgColor: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)',
    borderColor: '#3182ce',
    icon: <CiCircleInfo size={24} style={{ color: '#3182ce' }} />,
    confirmBg: 'linear-gradient(135deg, #3182ce 0%, #2c5282 100%)',
    titleColor: '#2c5282'
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div
        style={{
          background: config.bgColor,
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '400px',
          width: '90%',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          border: `2px solid ${config.borderColor}`,
          animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          {config.icon}
          <h3 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 'bold',
            color: config.titleColor
          }}>
            Confirm Action
          </h3>
        </div>

        <p style={{
          margin: '0 0 24px 0',
          fontSize: '14px',
          lineHeight: '1.5',
          color: '#4a5568'
        }}>
          {message}
        </p>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)',
              border: '1px solid #cbd5e0',
              color: '#4a5568',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              background: config.confirmBg,
              border: 'none',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s',
              boxShadow: '0 4px 8px rgba(49, 130, 206, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 6px 12px rgba(49, 130, 206, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 8px rgba(49, 130, 206, 0.3)';
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const SaveWorkspaceModal = ({
  show,
  handleClose,
  onSave,
  selectedIndicators = [],
  selectedLocations = [],
  allMapFeatures = [],
  mode = 'save'
}) => {
  const { user, isAuthenticated } = useAuth();
  const dispatch = useDispatch();

  // Get workspace state from Redux
  const isWorkspaceLoaded = useSelector(state => state.workspace.isWorkspaceLoaded);
  const hasUnsavedChanges = useSelector(state => state.workspace.hasUnsavedChanges);
  const workspaceId = useSelector(state => state.workspace.workspaceId);
  const loadedWorkspaceName = useSelector(state => state.workspace.loadedWorkspaceName);
  const savedChart = useSelector(state => state.workspace.savedCharts)

  // Determine modes FIRST (before using them anywhere)
  const isUpdateMode = (mode === 'save' && isWorkspaceLoaded && workspaceId) || (mode === 'update' && isWorkspaceLoaded && workspaceId);
  const isSaveAsMode = mode === 'saveAs';

  const [workspaceName, setWorkspaceName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Confirm state
  const [confirm, setConfirm] = useState(null);

  // Confirm helper function
  const showConfirm = (message, onConfirm) => {
    setConfirm({
      message,
      onConfirm: () => {
        setConfirm(null);
        onConfirm();
      },
      onCancel: () => {
        setConfirm(null);
      }
    });
  };

  // Set workspace name when modal opens and clear messages
  useEffect(() => {
    if (show) {
      if (isUpdateMode) {
        // Update mode - use loaded workspace name (readonly)
        setWorkspaceName(loadedWorkspaceName || '');
      } else {
        // Save/SaveAs mode - clear name for new workspace
        setWorkspaceName('');
      }
      // Clear error and success messages when modal opens
      setError('');
      setSuccessMessage('');
    }
  }, [show, isUpdateMode, loadedWorkspaceName]);

  // Check token when modal opens
  useEffect(() => {
    if (show) {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');

      if (!token || !userData) {
        // Handle authentication error silently or show in confirmation
        console.warn('Authentication token not found');
      }
    }
  }, [show]);

  const handleSave = async () => {
    // Clear previous errors
    setError('');
    setSuccessMessage('');

    // Enhanced validation
    if (!workspaceName || !workspaceName.trim()) {
      setError('Workspace name is required. Please enter a name for your workspace.');
      return;
    }

    if (workspaceName.trim().length < 2) {
      setError('Workspace name must be at least 2 characters long.');
      return;
    }

    // Check authentication more robustly
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');

    if (!token || !userData) {
      setError('You must be logged in to save workspaces. Please log in and try again.');
      return;
    }

    // Additional check: if isAuthenticated is false but we have token, 
    // it might be a timing issue, so we'll proceed with the save
    if (!isAuthenticated && (!token || !userData)) {
      setError('Authentication failed. Please log in again and try again.');
      return;
    }

    if (selectedIndicators.length === 0) {
      setError('Please select at least one indicator before saving the workspace.');
      return;
    }

    // Show confirmation dialog
    const confirmMessage = isUpdateMode
      ? `Are you sure you want to update workspace "${workspaceName}"? This will replace the existing workspace data.`
      : isSaveAsMode
        ? `Are you sure you want to save workspace "${workspaceName}" as a new workspace?`
        : `Are you sure you want to save workspace "${workspaceName}"? This will create a new workspace with your current selections.`;

    showConfirm(confirmMessage, async () => {
      await performSave();
    });
  };

  const performSave = async () => {
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Create comprehensive locations array - only add India if it's not already in the selected locations
      const allLocationsToSave = [...allMapFeatures.map(({ geometry, ...rest }) => rest)];

      // Check if India (code: 1000, geo_level: 1) is already in the selected locations
      const hasIndia = allMapFeatures.some(loc =>
        (loc.code === 1000 || loc.properties?.code === 1000) &&
        (loc.geo_level === 1 || loc.geo_level_code === 1 || loc.properties?.geo_level_code === 1)
      );

      // Only add India if it's not already selected
      if (!hasIndia) {
        allLocationsToSave.unshift({
          code: 1000,
          name: "India",
          geo_level: 1
        });
      }

      // Remove duplicates - use a combination of code and name for more accurate deduplication
      const uniqueLocations = allLocationsToSave.reduce((acc, current) => {
        // Create a unique identifier using code, geo_level, and name
        const currentCode = current.code || current.properties?.code;
        const currentGeoLevel = current.geo_level || current.geo_level_code || current.properties?.geo_level_code;
        const currentName = current.name || current.properties?.name;

        const currentId = `${currentCode}-${currentGeoLevel}-${currentName}`;

        const exists = acc.find(item => {
          const itemCode = item.code || item.properties?.code;
          const itemGeoLevel = item.geo_level || item.geo_level_code || item.properties?.geo_level_code;
          const itemName = item.name || item.properties?.name;
          const itemId = `${itemCode}-${itemGeoLevel}-${itemName}`;
          return itemId === currentId;
        });

        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);

      // Prepare workspace data
      const workspaceData = {
        name: workspaceName.trim(),
        indicators: selectedIndicators.length,
        locations: uniqueLocations.length,
        savedChart: savedChart,
        createdAt: new Date().toISOString(),
        savedIndicators: selectedIndicators.map(ind => ind),
        // savedIndicators: selectedIndicators.map(ind => ({
        //   indicator_code: ind.indicator_code,
        //   indicator_name: ind.indicator_name,
        //   dataset_name: ind.dataset_name,
        //   agency_name: ind.agency_name,
        //   sector_name: ind.sector_name,
        //   sub_sector_name: ind.sub_sector_name,
        //   sub_sub_sector_name: ind.sub_sub_sector_name
        // })),
        savedLocations: uniqueLocations
      };

      let response;

      if (isUpdateMode) {
        // UPDATE existing workspace
        response = await updateWorkspaceContent(workspaceId, {
          ws_name: workspaceData.name,
          ws_data: JSON.stringify(workspaceData),
          indicator_count: selectedIndicators.length,
          location_count: uniqueLocations.length
        });

        // Mark as saved in Redux
        dispatch(markAsSaved({
          isNew: false,
          name: workspaceData.name,
          id: workspaceId
        }));

      } else {
        // CREATE new workspace (for both 'save' and 'saveAs' modes)
        response = await createWorkspace({
          ws_name: workspaceData.name,
          ws_data: JSON.stringify(workspaceData),
          indicator_count: selectedIndicators.length,
          location_count: uniqueLocations.length
        });
      }

      // Handle response
      let workspaceIdResult;
      let savedWorkspaceName;

      if (response && response.data && response.data.workspace) {
        workspaceIdResult = response.data.workspace.id;
        savedWorkspaceName = response.data.workspace.ws_name || workspaceData.name;
      } else if (response && response.workspace) {
        workspaceIdResult = response.workspace.id;
        savedWorkspaceName = response.workspace.ws_name || workspaceData.name;
      } else if (response && response.id) {
        workspaceIdResult = response.id;
        savedWorkspaceName = workspaceData.name;
      } else {
        workspaceIdResult = workspaceId || Date.now();
        savedWorkspaceName = workspaceData.name;
      }

      // Call onSave callback with enhanced workspace data
      if (onSave) {
        onSave({
          id: workspaceIdResult,
          name: savedWorkspaceName,
          ...workspaceData,
          isUpdate: isUpdateMode,
          isSaveAs: isSaveAsMode
        });
      }

      // Show success message
      const successMsg = isUpdateMode
        ? `Workspace "${savedWorkspaceName}" updated successfully!`
        : `Workspace "${savedWorkspaceName}" saved successfully!`;
      setSuccessMessage(successMsg);

      // Close modal after showing success message (give user enough time to read it)
      // Using 5.5 seconds (5500ms) to ensure users can comfortably read the success message
      setTimeout(() => {
        handleModalClose();
      }, 5500);

    } catch (error) {
      // Don't close modal on error - let user see the error message and try again
      console.error('Workspace save/update error:', error);

      // Format user-friendly error messages
      let errorMessage = 'Failed to save workspace. Please try again.';

      if (error.status === 401) {
        errorMessage = 'Your session has expired. Please log in again and try again.';
      } else if (error.status === 403) {
        errorMessage = 'You do not have permission to perform this action. Please check your account permissions.';
      } else if (error.status === 404) {
        errorMessage = isUpdateMode
          ? 'Workspace not found. It may have been deleted. Please refresh and try again.'
          : 'Resource not found. Please try again.';
      } else if (error.status === 409) {
        errorMessage = `A workspace with the name "${workspaceName}" already exists. Please choose a different name.`;
      } else if (error.status === 500) {
        errorMessage = 'Server error occurred. Please try again later or contact support if the problem persists.';
      } else if (error.message) {
        // Check for specific error messages from backend
        if (error.message.includes('duplicate') || error.message.includes('already exists')) {
          errorMessage = `A workspace with the name "${workspaceName}" already exists. Please choose a different name.`;
        } else if (error.message.includes('invalid') || error.message.includes('Invalid')) {
          errorMessage = 'Invalid workspace data. Please check your selections and try again.';
        } else if (error.message.includes('Network error') || error.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else {
          errorMessage = error.message;
        }
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setWorkspaceName('');
    setIsLoading(false);
    setError('');
    setSuccessMessage('');
    handleClose();
  };

  // Calculate total locations for display - use same logic as save
  const calculateDisplayCount = () => {
    // Check if India is already in the selected locations
    const hasIndia = allMapFeatures.some(loc =>
      (loc.code === 1000 || loc.properties?.code === 1000) &&
      (loc.geo_level === 1 || loc.geo_level_code === 1 || loc.properties?.geo_level_code === 1)
    );

    // Return count based on whether India is already included
    return hasIndia ? allMapFeatures.length : allMapFeatures.length + 1;
  };

  const totalAvailableLocations = calculateDisplayCount();

  // Determine modal content based on mode
  const modalTitle = isUpdateMode ? 'Update Workspace' : 'Save Workspace';
  const buttonText = isUpdateMode ? 'Update Workspace' : 'Save Workspace';
  const buttonLoadingText = isUpdateMode ? 'Updating...' : 'Saving...';

  return (
    <>
      <Modal show={show} onHide={handleModalClose} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Success Message */}
          {successMessage && (
            <Alert variant="success" className="mb-3" dismissible onClose={() => setSuccessMessage('')}>
              {successMessage}
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert variant="danger" className="mb-3" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Workspace Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter workspace name"
                value={workspaceName}
                onChange={(e) => {
                  setWorkspaceName(e.target.value);
                  // Clear error when user starts typing
                  if (error) {
                    setError('');
                  }
                }}
                disabled={isLoading}
                readOnly={isUpdateMode} // Make readonly in update mode
                maxLength={50}
              />
              <Form.Text className="text-muted">
                {isUpdateMode
                  ? 'Updating existing workspace'
                  : isSaveAsMode
                    ? 'Enter a new name for the workspace copy'
                    : 'Choose a unique name for your workspace (max 50 characters)'
                }
              </Form.Text>
            </Form.Group>

            {/* Workspace Summary */}
            <div className="bg-light p-3 rounded mb-3">
              <h6>
                {isUpdateMode ? 'Update Summary:' : isSaveAsMode ? 'Save As Summary:' : 'Workspace Summary:'}
              </h6>
              <div className="d-flex justify-content-between mb-2">
                <span>Indicators:</span>
                <span className="badge bg-primary">{selectedIndicators.length}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Locations:</span>
                <span className="badge bg-primary">{totalAvailableLocations}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>Charts:</span>
                <span className="badge bg-primary">{savedChart.length}</span>
              </div>
              <small className={`mt-2 d-block ${isUpdateMode ? 'text-dark' : isSaveAsMode ? 'text-info' : 'text-info'}`}>
                {isUpdateMode
                  ? `⚡ Updating workspace "${loadedWorkspaceName}" with current selections`
                  : isSaveAsMode

                }
              </small>
            </div>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={handleModalClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={isUpdateMode ? "primary" : "primary"}
            onClick={handleSave}
            disabled={isLoading || !workspaceName.trim()}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {buttonLoadingText}
              </>
            ) : (
              buttonText
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Dialog */}
      {confirm && (
        <CustomConfirm
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={confirm.onCancel}
        />
      )}

      <style jsx>{`
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes scaleIn {
        from { 
          opacity: 0; 
          transform: scale(0.8); 
        }
        to { 
          opacity: 1; 
          transform: scale(1); 
        }
      }
    `}</style>
    </>
  );
};

export default SaveWorkspaceModal;